import { IconButton } from "@/components/form"
import Icon from "@/components/Icon"
import { getOperatorText, Operator, type Filter } from "@/models"
import { faDatabase, faEdit, faGear, faTag, faTrashAlt } from "@fortawesome/free-solid-svg-icons"
import { css } from "vite-css-in-js"
import { computed, type ShallowReactive } from "vue"
import { defineComponent, inferProps, type ReactiveComponent } from "vue-utils"
import { getDisplayComponent } from "../../Helpers"

interface Props {
	filter: ShallowReactive<Filter>
	editFilter(filter: Filter): void
	removeFilter(filter: Filter): void
}

const filterCardFormatting = css`
	display: flex;
	flex-direction: column;
	background-color: #fff;
	justify-content: space-between;

	width: 100%;

	border: 2px solid black;
	border-radius: 0.5rem;
	box-shadow: 0.35rem 0.35rem 0.35rem #8f8f8f;
	margin-bottom: 0.75rem;
`

const infoDivFormatting = css`
	display: flex;
	flex-direction: column;
	word-break: break-word;
	padding: 0.75rem 0rem 0rem 0.75rem;
	gap: 0.5rem;

	h6 {
		margin-bottom: 0;
	}
`

const attributeStyling = css`
	display: grid;
	grid-template-columns: max-content max-content 1fr;
	gap: 0.5rem;
	align-items: end;

	h6 {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`

const filterCardButtonFormatting = css`
	display: flex;
	flex-direction: row;
	align-content: stretch;
	margin-top: 0.5rem;

	button {
		width: 50%;
		border-radius: 0%;
	}
`

const AdvancedFilterCard: ReactiveComponent<Props> = (props) => {
	const displayComponent = computed(() => getDisplayComponent(props.filter.Property.usedSchema, props.filter.Operator))

	function renderDisplay() {
		const DisplayComponent = displayComponent.value
		return <DisplayComponent value={props.filter.Value} />
	}

	return () => (
		<div class={filterCardFormatting}>
			<div class={infoDivFormatting}>
				<div class={attributeStyling}>
					<Icon icon={faTag} />
					<h6>Field:</h6>
					<h6 style="font-weight: bold;" title={props.filter.Property.formatName()}>
						{props.filter.Property.formatName()}
					</h6>
				</div>
				<div class={attributeStyling}>
					<Icon icon={faGear} />
					<h6>Operator:</h6>
					<h6 style="font-weight: bold;">{getOperatorText(props.filter.Operator)}</h6>
				</div>

				{props.filter.Operator !== Operator.Exists && props.filter.Operator !== Operator.NotExists && (
					<div
						class={`d-flex ${props.filter.Operator === Operator.In || props.filter.Operator === Operator.NotIn ? "flex-column" : "flex-row"}`}
					>
						<div style="display: flex; gap: 0.5rem;">
							<Icon icon={faDatabase} />
							<h6 class="me-2">
								{props.filter.Operator === Operator.In || props.filter.Operator === Operator.NotIn
									? "Values:"
									: "Value:"}
							</h6>
						</div>
						<h6 style="font-weight: bold;">{renderDisplay()}</h6>
					</div>
				)}
			</div>
			<div class={filterCardButtonFormatting}>
				<IconButton
					icon={faEdit}
					color="primary"
					onClick={() => props.editFilter(props.filter)}
					style="border-bottom-left-radius: 0.375rem;"
				/>
				<IconButton
					icon={faTrashAlt}
					color="danger"
					onClick={() => props.removeFilter(props.filter)}
					style="border-bottom-right-radius: 0.375rem;"
				/>
			</div>
		</div>
	)
}

export default defineComponent(AdvancedFilterCard, inferProps<Props>())
